import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import UserMenu from './UserMenu';

const Navigation: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isSitesOpen, setIsSitesOpen] = useState(false);
  const location = useLocation();
  const sitesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();

    const handleScroll = () => setIsScrolled(window.scrollY > 10);

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Handle click outside for desktop dropdowns only
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sitesRef.current && !sitesRef.current.contains(e.target as Node)) {
        setIsSitesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
    setIsProductsOpen(false);
    setIsSitesOpen(false);
  }, [location]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  const isArticlePage = location.pathname.startsWith('/article/');
  const shouldBeSticky = !(isMobile && isArticlePage);

  return (
    <>
    <nav className={`bg-white ${shouldBeSticky ? 'sticky top-0' : ''} z-50 transition-shadow duration-200 ${isScrolled ? 'shadow-md' : 'md:border-b md:border-slate-200'}`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-12 md:h-16">
          {/* Logo + Sites Dropdown */}
          <div className="flex-shrink-0 relative flex items-center gap-0.5" ref={sitesRef}>
            <Logo size="md" />
            <button
              onClick={() => setIsSitesOpen(!isSitesOpen)}
              className="p-1 rounded hover:bg-slate-100 transition-colors"
              aria-label="Our sites"
            >
              <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isSitesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isSitesOpen && (
              <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Our Sites</div>
                <a href="https://savedelete.com/" className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 font-medium">
                  <span className="w-5 h-5 rounded bg-slate-900 flex items-center justify-center text-white text-[10px] font-bold">SD</span>
                  SaveDelete
                </a>
                <a href="http://yourfinances.in/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <span className="w-5 h-5 rounded bg-emerald-600 flex items-center justify-center text-white text-[10px] font-bold">YF</span>
                  Your Finances
                </a>
                <a href="https://playkorner.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <span className="w-5 h-5 rounded bg-purple-600 flex items-center justify-center text-white text-[10px] font-bold">PK</span>
                  PlayKorner
                </a>
                <a href="http://ilovejaipur.city/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <span className="w-5 h-5 rounded bg-pink-600 flex items-center justify-center text-white text-[10px] font-bold">IJ</span>
                  I Love Jaipur City
                </a>
                <a href="https://tattoosluv.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <span className="w-5 h-5 rounded bg-rose-600 flex items-center justify-center text-white text-[10px] font-bold">TL</span>
                  Tattoos Luv
                </a>
                <a href="https://livingimpressive.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <span className="w-5 h-5 rounded bg-amber-600 flex items-center justify-center text-white text-[10px] font-bold">LI</span>
                  Living Impressive
                </a>
                <a href="https://cooldigital.photography/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <span className="w-5 h-5 rounded bg-sky-600 flex items-center justify-center text-white text-[10px] font-bold">CD</span>
                  Cool Digital Photography
                </a>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {/* Home Icon */}
            <Link
              to="/"
              className={`p-2 rounded-md transition-colors ${
                location.pathname === '/'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
              aria-label="Home"
              title="Home"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </Link>

            {/* News — the site's own news product, and until 2026-09-05 it appeared in
                no navigation anywhere: not the header, not the footer. The article
                template carries ~60% of human traffic and had zero links into /news. */}
            <Link
              to="/news/"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname.startsWith('/news')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              News
            </Link>

            {/* Tools */}
            <Link
              to="/tools/"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname.startsWith('/tools')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              Tools
            </Link>

            {/* Guides */}
            <Link
              to="/guides"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname.startsWith('/guides')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              Guides
            </Link>
            {/* Free Business Tools hub */}
            <Link
              to="/free-business-tools/"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                location.pathname.startsWith('/free-business-tools/')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              Business Tools
            </Link>

            {/* iOS apps showcase */}
            <Link
              to="/apps/"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname.startsWith('/apps')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              Apps
            </Link>

            {/* Newsletter */}
            <Link
              to="/newsletter"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname.startsWith('/newsletter')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              Newsletter
            </Link>

          </div>

          {/* Desktop Search Icon + User Menu */}
          <div className="hidden md:flex items-center space-x-2">
            {isSearchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center gap-1">
                <div className="relative">
                  <input
                    type="text"
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-44 lg:w-56 pl-9 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSearchOpen(false)}
                  className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                  aria-label="Close search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </form>
            ) : (
              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-1.5 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                aria-label="Search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            )}
            <UserMenu />
          </div>

          {/* Mobile Actions: User, Menu */}
          <div className="flex md:hidden items-center space-x-1">
            <UserMenu />

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1.5 rounded-md text-slate-600 hover:bg-slate-50"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Search Bar (Toggleable) */}
      {isSearchOpen && (
        <div className="md:hidden px-4 py-3 bg-white border-b border-slate-200 animate-in slide-in-from-top duration-200">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <label htmlFor="nav-search" className="sr-only">Search articles</label>
              <input
                id="nav-search"
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </form>
        </div>
      )}

      {/* Mobile Menu Content */}
      {isMobileMenuOpen && (
        <div id="mobile-menu" className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3">
            <div className="space-y-1">
              <Link
                to="/"
                className={`block px-3 py-2 rounded-md text-sm font-medium ${
                  location.pathname === '/' ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Home
              </Link>

              <Link
                to="/news/"
                className={`block px-3 py-2 rounded-md text-sm font-medium ${
                  location.pathname.startsWith('/news') ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                News
              </Link>

              <Link
                to="/tools/"
                className={`block px-3 py-2 rounded-md text-sm font-medium ${
                  location.pathname.startsWith('/tools') ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Tools
              </Link>

              <Link
                to="/guides"
                className={`block px-3 py-2 rounded-md text-sm font-medium ${
                  location.pathname.startsWith('/guides') ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Guides
              </Link>
              <Link
                to="/free-business-tools/"
                className={`block px-3 py-2 rounded-md text-sm font-medium ${
                  location.pathname.startsWith('/free-business-tools/') ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Free Business Tools
              </Link>

              <Link
                to="/apps/"
                className={`block px-3 py-2 rounded-md text-sm font-medium ${
                  location.pathname.startsWith('/apps') ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Apps
              </Link>

              <Link
                to="/newsletter"
                className={`block px-3 py-2 rounded-md text-sm font-medium ${
                  location.pathname.startsWith('/newsletter') ? 'text-blue-600 bg-blue-50' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                Newsletter
              </Link>

            </div>
          </div>
        </div>
      )}
    </nav>

    {/* Mobile Sub-Header (Only visible on mobile, not fixed) */}
    <div className="md:hidden bg-slate-50 border-b border-slate-200 overflow-x-auto whitespace-nowrap scrollbar-hide">
      <div className="flex items-center px-4 py-1.5 space-x-4">
        {/* Search Icon */}
        <button
          onClick={() => setIsSearchOpen(!isSearchOpen)}
          className={`p-1.5 rounded-md transition-colors ${isSearchOpen ? 'text-blue-600 bg-blue-100' : 'text-slate-600 hover:bg-slate-200'}`}
          aria-label="Toggle search"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>

        <Link
          to="/tools/"
          className="text-[11px] font-bold text-slate-600 hover:text-blue-600 flex items-center gap-1.5 py-1"
        >
          Tools
        </Link>
        <Link
          to="/guides"
          className="text-[11px] font-bold text-slate-600 hover:text-blue-600 flex items-center gap-1.5 py-1"
        >
          Guides
        </Link>
        <Link
          to="/ai-tools/"
          className="text-[11px] font-bold text-slate-600 hover:text-blue-600 flex items-center gap-1.5 py-1"
        >
          AI Tools
        </Link>
        <Link
          to="/free-invoice-generator/"
          className="text-[11px] font-bold text-slate-600 hover:text-blue-600 flex items-center gap-1.5 py-1 whitespace-nowrap"
        >
          Invoice Generator
        </Link>

      </div>
    </div>
    </>
  );
};

export default Navigation;
